import React from 'react';
import { Box, TextField, MenuItem, Typography } from '@mui/material';
import { PromptOwnerType, PromptTypeType, PromptAccountScopeType, SkillApplicabilityType } from '../../../types/backend';
import { CREATABLE_PROMPT_TYPES, PROMPT_TYPE_LABELS, SKILL_APPLICABILITY_LABELS } from '../../../shared/constants/promptTypes';
import { PromptFormData } from '../hooks/usePromptManagement';

interface PromptSettingsProps {
  formData: PromptFormData;
  isNewPrompt: boolean;
  fieldErrors: Record<string, string | null>;
  onChange: <K extends keyof PromptFormData>(field: K, value: PromptFormData[K]) => void;
  /** Hide the description field when it's surfaced elsewhere (e.g. a skill's trigger on the Write tab). */
  hideDescription?: boolean;
}

const SKILL_TYPES: PromptTypeType[] = ['skill', 'learned_skill'];
const labelCaption = { display: 'block', mb: 0.25 } as const;

/**
 * The prompt's definition — presented inline, in the studio idiom (no accordion).
 * Immutable facts (name/type/owner) only appear while creating; once the prompt exists
 * they live in the identity line. Behavioural fields (account scope / agent type) stay
 * editable and visible because they change what the agent does.
 */
const PromptSettings: React.FC<PromptSettingsProps> = ({
  formData,
  isNewPrompt,
  fieldErrors,
  onChange,
  hideDescription = false,
}) => {
  const isPlaybook = formData.type === 'playbook';
  const isSkill = SKILL_TYPES.includes(formData.type);
  const creatableInstructionTypes = CREATABLE_PROMPT_TYPES.filter((t) => t !== 'skill');

  const showDescription = !hideDescription;
  const showCreationFields = isNewPrompt;
  const showAccountScope = isPlaybook;
  const showAgentType = isSkill;

  if (!showDescription && !showCreationFields && !showAccountScope && !showAgentType) return null;

  return (
    <Box sx={{ px: 3, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start' }}>
        {showDescription && (
          <Box sx={{ minWidth: 260, flex: '2 1 320px' }}>
            <Typography variant="caption" color="text.secondary" sx={labelCaption}>Description</Typography>
            <TextField
              value={formData.description}
              onChange={(e) => onChange('description', e.target.value)}
              error={!!fieldErrors.description}
              helperText={fieldErrors.description || undefined}
              placeholder="Short summary, shown in the list"
              size="small"
              fullWidth
            />
          </Box>
        )}

        {showCreationFields && isSkill && (
          <Box sx={{ minWidth: 240, flex: '1 1 240px' }}>
            <Typography variant="caption" color="text.secondary" sx={labelCaption}>Skill name — the pool identifier</Typography>
            <TextField
              value={formData.name}
              onChange={(e) => onChange('name', e.target.value)}
              error={!!fieldErrors.name}
              helperText={fieldErrors.name || 'A customer skill with this name overrides the global one in the pool'}
              placeholder="aurora-failover"
              size="small"
              fullWidth
            />
          </Box>
        )}

        {showCreationFields && !isSkill && (
          <Box sx={{ minWidth: 180, flex: '1 1 180px' }}>
            <Typography variant="caption" color="text.secondary" sx={labelCaption}>Type</Typography>
            <TextField
              select
              value={formData.type}
              onChange={(e) => onChange('type', e.target.value as PromptTypeType)}
              size="small"
              fullWidth
            >
              {creatableInstructionTypes.map((t) => (
                <MenuItem key={t} value={t}>{PROMPT_TYPE_LABELS[t]}</MenuItem>
              ))}
            </TextField>
          </Box>
        )}

        {showCreationFields && (
          <Box sx={{ minWidth: 160, flex: '1 1 160px' }}>
            <Typography variant="caption" color="text.secondary" sx={labelCaption}>Owner</Typography>
            <TextField
              select
              value={formData.owner}
              onChange={(e) => onChange('owner', e.target.value as PromptOwnerType)}
              size="small"
              fullWidth
            >
              <MenuItem value="customer">This Customer</MenuItem>
              <MenuItem value="global">Cloud2 Global</MenuItem>
            </TextField>
          </Box>
        )}

        {showAccountScope && (
          <Box sx={{ minWidth: 220, flex: '1 1 220px' }}>
            <Typography variant="caption" color="text.secondary" sx={labelCaption}>Account scope</Typography>
            <TextField
              select
              value={formData.accountScope}
              onChange={(e) => onChange('accountScope', e.target.value as PromptAccountScopeType)}
              size="small"
              fullWidth
            >
              <MenuItem value="none">None (Knowledge-only)</MenuItem>
              <MenuItem value="operations">Operations Account</MenuItem>
              <MenuItem value="customer">Customer Account(s)</MenuItem>
              <MenuItem value="both">Both (Operations + Customer)</MenuItem>
            </TextField>
          </Box>
        )}

        {showAgentType && (
          <Box sx={{ minWidth: 240, flex: '1 1 240px' }}>
            <Typography variant="caption" color="text.secondary" sx={labelCaption}>Available to — joins this pool</Typography>
            <TextField
              select
              value={formData.agentType}
              onChange={(e) => onChange('agentType', e.target.value as SkillApplicabilityType)}
              size="small"
              fullWidth
            >
              {(Object.keys(SKILL_APPLICABILITY_LABELS) as SkillApplicabilityType[]).map((a) => (
                <MenuItem key={a} value={a}>{SKILL_APPLICABILITY_LABELS[a]}</MenuItem>
              ))}
            </TextField>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PromptSettings;
